// Signing in — to MEAL HAT's Firebase project, not one of this app's own.
//
// Deliberate, and the whole architecture: the hat, its grocery catalog, its
// shopping list and its fridge all live in project `meal-hat`, and a lesson
// is only useful if it can read what is in the house and put what is missing
// on the list Matt already shops from. A second project would mean copying
// all of that or reading none of it. Same shape as Movie Requests signing
// into Movie Hat.
//
// The config below is the same public config Meal Hat ships in its bundle.
// Every origin this app is served from has to be on that project's
// Authorized Domains list (Authentication → Settings) or the Google popup
// refuses to open; cook.mealhat.com is added by scripts/setup-domain.sh
// (through the Firebase CLI's own login) or by hand in the console.
import { initializeApp } from 'firebase/app';
import {
  getAuth, GoogleAuthProvider, signInWithPopup, signInWithCustomToken, signInWithEmailAndPassword,
  signOut as firebaseSignOut, onAuthStateChanged, setPersistence, browserLocalPersistence, connectAuthEmulator
} from 'firebase/auth';
import { ref, readonly } from 'vue';

export const firebaseConfig = {
  apiKey: 'AIzaSyAgRwQfTJo00U69by1TXcL5jQU9QNWZLAg',
  authDomain: 'meal-hat.firebaseapp.com',
  projectId: 'meal-hat',
  storageBucket: 'meal-hat.appspot.com',
  messagingSenderId: '871807065045',
  appId: '1:871807065045:web:eaaf302a198f18c41a3b5c',
  databaseURL: 'https://meal-hat-default-rtdb.firebaseio.com'
};

initializeApp(firebaseConfig);

// Local testing against the Firebase EMULATORS (Meal Hat's own arrangement).
// `yarn serve:emulated` sets VUE_APP_FIREBASE_EMULATORS=1 at build time, so a
// production bundle contains `if (false)` and no emulator path at all. The
// auth emulator is what makes a signed-in browser run possible without the
// Google popup and without any service-account key: `scripts/seed-emulator.mjs`
// creates a tester there, and `?emulatorSignIn=1` signs it in below.
export const useEmulators = process.env.VUE_APP_FIREBASE_EMULATORS === '1';
export const EMULATOR_TESTER = { email: 'tester@example.com', password: 'tester-password' };
// The browser being driven is not always on this machine (Matt's Chrome
// over remote control refuses localhost outright), so the emulator host is
// settable: VUE_APP_EMULATOR_HOST=192.168.4.36 yarn serve:emulated.
export const EMULATOR_HOST = process.env.VUE_APP_EMULATOR_HOST || 'localhost';
if (useEmulators) {
  connectAuthEmulator(getAuth(), `http://${EMULATOR_HOST}:9099`, { disableWarnings: true });
}

// The signed-in user, or null. `authSettled` is the important one: for the
// first few hundred milliseconds after load Firebase has not finished
// restoring the session and `user` is null for somebody perfectly signed
// in. Anything that routes on who you are waits for this.
export const user = ref(null);
export const authSettled = ref(false);

let started = false;

export function watchAuth () {
  if (started) return;
  started = true;
  setPersistence(getAuth(), browserLocalPersistence).catch(() => {});
  // The test door (house pattern): `?testToken=<custom token>` minted by
  // `yarn mint-test-token` signs the dedicated tester in with no popup, so a
  // browser can be driven through the signed-in app. The token is single-use
  // and short-lived, and it is stripped from the address bar at once.
  // Harmless in production: a token Firebase does not recognise just fails
  // to sign in, and there is nothing else on this door.
  const params = new URLSearchParams(window.location.search);
  const testToken = params.get('testToken');
  if (testToken) {
    params.delete('testToken');
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
    signInWithCustomToken(getAuth(), testToken).catch((error) => console.error('Test sign-in failed', error));
  }
  if (useEmulators && params.get('emulatorSignIn')) {
    signInWithEmailAndPassword(getAuth(), EMULATOR_TESTER.email, EMULATOR_TESTER.password)
      .catch((error) => console.error('Emulator sign-in failed', error));
  }
  onAuthStateChanged(
    getAuth(),
    (account) => {
      user.value = account || null;
      authSettled.value = true;
    },
    (error) => {
      console.error('Auth listener failed', error);
      authSettled.value = true;
    }
  );
}

export async function signIn () {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  await signInWithPopup(getAuth(), provider);
}

export function signOut () {
  return firebaseSignOut(getAuth());
}

export const currentUser = readonly(user);
